import { useContext, useEffect, useState, useMemo } from 'react';
import { Context as StoreContext } from '../../providers/StoreContextProvider';
import { Context as StoreUserContext } from '../../providers/StoreUserContextProvider';
import { Context as AuthContext } from '../../providers/AuthContextProvider';
import { Context as HelperContext } from '../../providers/HelperContextProvider';
import {
  createFetchStoreCart,
  getCartProductError,
  uploadProductAnswerMedia
} from './webstoreCheckoutUtilities';
import { useCartShippingController } from './useCartShippingController';
import {
  matchInputAnswers,
  matchDesignAnswers,
  useCartStorageController
} from './useCartStorageController';
import {
  getEcomStoreProductSelectOptions,
  getSelectedEcomStoreProductDesigns,
  getSelectedEcomStoreProductVariant,
  isCartVariantUnavailable
} from '../webstoreHelper';
import { triggerNotification } from '../notification';
import imageNotAvailablePlaceholder from '../../images/image-not-available.png';
import { webstoreTrackingHelper } from '../webstoreTrackingHelper';

const useCartController = ({
  isAdminCheckout = false,
  pkEcomOrder = null,
  fetchShippingOnAddressChange = false
} = {}) => {
  const { state: authState } = useContext(AuthContext);
  const {
    state: helperState,
    toggleWebstoreCheckoutSummaryDrawer,
    toggleWebstoreContinueAsGuestModal
  } = useContext(HelperContext);
  const {
    state,
    addProductsToCart,
    updateEcomStoreCartProducts,
    removeProductsFromCart,
    checkoutCart,
    checkoutGuestCart,
    fetchEcomStoreCart,
    fetchEcomStoreGuestCart
  } = useContext(StoreUserContext);
  const {
    state: adminState,
    mockEcomStoreCartCheckout,
    checkoutAdminCart,
    fetchEcomStoreOrderAdminCart
  } = useContext(StoreContext);
  const {
    getGuestCartStorage,
    setCartSecureId,
    setCartFundraiser,
    resetCart,
    setCartEmail,
    addGuestCartProductsAndPackages,
    updateGuestCartProductsAndPackages,
    onFetchGuestCartCallback
  } = useCartStorageController({ isAdminCheckout, pkEcomOrder });
  const {
    shippingAddress,
    shippingCostsLoading,
    shippingCosts,
    onShippingCallback,
    onChangeShippingAddress
  } = useCartShippingController({
    isAdminCheckout,
    pkEcomOrder,
    fetchShippingOnAddressChange
  });

  const { loading, value: ecomStoreCart } = isAdminCheckout
    ? adminState.ecomOrderAdminCart
    : authState.isAuthenticated
    ? state.ecomStoreCart
    : state.ecomStoreGuestCart;
  const isCartFetching = loading || !authState.tokenAttempted;

  const fetchStoreCart = createFetchStoreCart({
    isAdminCheckout,
    authState,
    getGuestCartStorage,
    onShippingCallback,
    onFetchGuestCartCallback,
    fetchEcomStoreCart,
    fetchEcomStoreGuestCart,
    fetchEcomStoreOrderAdminCart
  });

  const addStoreProducts = (
    pkEcomStore,
    {
      ecomStoreCartProducts = [],
      ecomStoreCartPackages = [],
      includeShipping = false,
      removeUnavailable = false
    },
    onSuccessCallback = null,
    onErrorCallback = null
  ) => {
    const cart = getGuestCartStorage(pkEcomStore);

    const cartPackagesWithIndexes = ecomStoreCartPackages
      .filter((p) => !!p.ecomStoreCartProducts)
      .map((p, index) => ({
        ...p,
        ecomStoreCartProducts: p.ecomStoreCartProducts.map((e, index2) => ({
          ...e,
          searchIndex: `${index}-${index2}`
        }))
      }));

    const products = [
      ...ecomStoreCartProducts,
      ...cartPackagesWithIndexes.flatMap((p) => p.ecomStoreCartProducts)
    ];
    const cartProductErrors = products
      .map((c) => getCartProductError(c))
      .filter((e) => !!e);

    if (cartProductErrors.length > 0) {
      onErrorCallback(cartProductErrors[0], 'input');
    }
    else {
      uploadProductAnswerMedia(
        pkEcomStore,
        products,
        (requestedStoreProducts) => {
          const requestData = {
            ecomStoreCartProducts: requestedStoreProducts.filter(
              (p) => !p.fkEcomStoreProductPackage
            ),
            ecomStoreCartPackages: cartPackagesWithIndexes.map((p) => ({
              ...p,
              ecomStoreCartProducts: requestedStoreProducts.filter(
                (rp) =>
                  rp.fkEcomStoreProductPackage ===
                    p.fkEcomStoreProductPackage &&
                  p.ecomStoreCartProducts.some(
                    (c) => c.searchIndex === rp.searchIndex
                  )
              )
            })),
            removeUnavailable,
            includeShipping,
            ...(includeShipping
              ? { ecomCustomerAddress: cart.shippingAddress }
              : {})
          };

          if (!isAdminCheckout && authState.isAuthenticated) {
            addProductsToCart(
              pkEcomStore,
              requestData,
              (d) => {
                webstoreTrackingHelper.addStoreProductToCart(
                  d,
                  requestData,
                  state.ecomStoreTheme.value?.ecomStore
                );

                toggleWebstoreCheckoutSummaryDrawer({ isOpen: true });
                if (onSuccessCallback) {
                  onSuccessCallback(d);
                }
                onShippingCallback(pkEcomStore, d);
              },
              (e) => onErrorCallback(e)
            );
          }
          else if (
            !isAdminCheckout &&
            cart.ecomStoreCartProducts.length === 0 &&
            !helperState.webstore.continueAsGuestModal.hasBeenShown
          ) {
            toggleWebstoreContinueAsGuestModal({
              isOpen: true,
              formData: {
                ecomStoreCartProducts: requestData.ecomStoreCartProducts,
                ecomStoreCartPackages: requestData.ecomStoreCartPackages,
                includeShipping,
                removeUnavailable
              }
            });
            onSuccessCallback();
          }
          else {
            const {
              ecomStoreCartProducts: updatedCartProducts,
              ecomStoreCartPackages: updatedCartProductPackages
            } = addGuestCartProductsAndPackages(cart, requestData);

            fetchStoreCart(
              pkEcomStore,
              {
                ecomStoreCartProducts: [...updatedCartProducts],
                ecomStoreCartPackages: [...updatedCartProductPackages],
                removeUnavailable,
                includeShipping
              },
              (data) => {
                if (!isAdminCheckout) {
                  webstoreTrackingHelper.addStoreProductToCart(
                    data,
                    requestData,
                    state.ecomStoreTheme.value?.ecomStore
                  );
                }

                toggleWebstoreCheckoutSummaryDrawer({ isOpen: true });
                if (onSuccessCallback) {
                  onSuccessCallback(data);
                }
              },
              (e) => onErrorCallback(e)
            );
          }
        },
        onErrorCallback
      );
    }
  };

  const updateStoreCartProducts = (
    pkEcomStore,
    {
      ecomStoreCartProducts = [],
      ecomStoreCartPackages = [],
      includeShipping = false,
      removeUnavailable = false
    },
    onSuccessCallback = null,
    onErrorCallback = null
  ) => {
    const cart = getGuestCartStorage(pkEcomStore);

    const cartPackagesWithIndexes = ecomStoreCartPackages.map((p, index) => ({
      ...p,
      ecomStoreCartProducts:
        p.ecomStoreCartProducts?.map((e, index2) => ({
          ...e,
          searchIndex: `${index}-${index2}`
        })) || []
    }));

    const products = [
      ...ecomStoreCartProducts,
      ...cartPackagesWithIndexes.flatMap((p) => p.ecomStoreCartProducts)
    ];
    const cartProductErrors = products
      .map((c) => getCartProductError(c))
      .filter((e) => !!e);

    if (cartProductErrors.length > 0) {
      onErrorCallback(cartProductErrors[0], 'input');
    }
    else {
      uploadProductAnswerMedia(
        pkEcomStore,
        products,
        (requestedStoreProducts) => {
          const requestData = {
            skipLoading: true,
            ecomStoreCartProducts: requestedStoreProducts.filter(
              (p) => !p.fkEcomStoreProductPackage
            ),
            ecomStoreCartPackages: cartPackagesWithIndexes.map((p) => ({
              ...p,
              ecomStoreCartProducts: requestedStoreProducts.filter(
                (rp) =>
                  rp.fkEcomStoreProductPackage ===
                    p.fkEcomStoreProductPackage &&
                  p.ecomStoreCartProducts.some(
                    (c) => c.searchIndex === rp.searchIndex
                  )
              )
            })),
            removeUnavailable,
            includeShipping,
            ...(includeShipping
              ? { ecomCustomerAddress: cart.shippingAddress }
              : {})
          };

          if (!isAdminCheckout && authState.isAuthenticated) {
            updateEcomStoreCartProducts(
              pkEcomStore,
              requestData,
              (data) => {
                webstoreTrackingHelper.customizeStoreCartProducts(
                  data,
                  requestData,
                  state.ecomStoreTheme.value?.ecomStore
                );

                onShippingCallback(pkEcomStore, data);
                if (onSuccessCallback) {
                  onSuccessCallback(data);
                }
              },
              (e) => onErrorCallback(e)
            );
          }
          else {
            const {
              ecomStoreCartProducts: updatedGuestProducts,
              ecomStoreCartPackages: updatedGuestPackages
            } = updateGuestCartProductsAndPackages(cart, requestData);

            fetchStoreCart(
              pkEcomStore,
              {
                ...requestData,
                ecomStoreCartProducts: [...updatedGuestProducts],
                ecomStoreCartPackages: [...updatedGuestPackages]
              },
              (data) => {
                if (!isAdminCheckout) {
                  webstoreTrackingHelper.customizeStoreCartProducts(
                    data,
                    requestData,
                    state.ecomStoreTheme.value?.ecomStore
                  );
                }
                if (onSuccessCallback) {
                  onSuccessCallback(data);
                }
              },
              (e) => onErrorCallback(e)
            );
          }
        },
        onErrorCallback
      );
    }
  };

  const removeStoreProducts = (
    pkEcomStore,
    {
      pkEcomStoreCartProducts = [],
      pkEcomStoreCartPackages = [],
      includeShipping = false
    },
    onSuccessCallback,
    onErrorCallback
  ) => {
    const cart = getGuestCartStorage(pkEcomStore);

    if (!isAdminCheckout && authState.isAuthenticated) {
      removeProductsFromCart(
        pkEcomStore,
        {
          pkEcomStoreCartProducts,
          pkEcomStoreCartPackages,
          ...(includeShipping
            ? { ecomCustomerAddress: cart.shippingAddress }
            : {})
        },
        (data) => {
          onShippingCallback(pkEcomStore, data);
          if (onSuccessCallback) {
            onSuccessCallback(data);
          }
        },
        onErrorCallback
      );

      return;
    }

    // guest cart
    const products = cart.ecomStoreCartProducts.filter(
      (f) =>
        !pkEcomStoreCartProducts.some((p) => p === f.pkEcomStoreCartProduct)
    );
    const packages = cart.ecomStoreCartPackages.filter(
      (f) =>
        !pkEcomStoreCartPackages.some((p) => p === f.pkEcomStoreCartPackage)
    );
    fetchStoreCart(
      pkEcomStore,
      {
        skipLoading: true,
        ecomStoreCartProducts: [...products],
        ecomStoreCartPackages: [...packages],
        includeShipping
      },
      (data) => {
        if (onSuccessCallback) {
          onSuccessCallback(data);
        }
      },
      onErrorCallback
    );
  };

  const removeUnavailableStoreCartProducts = (
    pkEcomStore,
    { includeShipping = false },
    onSuccessCallback,
    onErrorCallback
  ) => {
    updateStoreCartProducts(
      pkEcomStore,
      { includeShipping, removeUnavailable: true },
      onSuccessCallback,
      onErrorCallback
    );
  };

  const onShowCartProductCustomization = ({
    ecomStoreCartProduct,
    ecomStoreCartPackage
  }) => {
    toggleWebstoreCheckoutSummaryDrawer({
      ...helperState.webstore.checkoutSummaryDrawer,
      showProductCustomization: true,
      ecomStoreCartProduct,
      ecomStoreCartPackage
    });
  };

  const checkoutStoreCart = (
    pkEcomStore,
    {
      email,
      installments,
      shippingOptions,
      fkEcomStoreRosterPlayer,
      fundraiserCustomInput,
      redirectPath
    },
    onSuccessCallback,
    onErrorCallback
  ) => {
    const cart = getGuestCartStorage(pkEcomStore);

    if (
      ecomStoreCart.checkoutCart.totalInCents !== 0 &&
      ecomStoreCart.checkoutCart.totalInCents / 100 < 5
    ) {
      onErrorCallback('Minimum order amount of $5 required');
    }
    else if (isAdminCheckout) {
      checkoutAdminCart(
        pkEcomStore,
        {
          email,
          ecomCustomerAddress: cart.shippingAddress,
          shippingOptions,
          installments,
          ecomStoreCartPackages: cart.ecomStoreCartPackages,
          ecomStoreCartProducts: cart.ecomStoreCartProducts,
          fkEcomStoreRosterPlayer,
          fundraiserCustomInput,
          redirectPath
        },
        (data) => {
          setCartSecureId(pkEcomStore, data.secureId);
          if (onSuccessCallback) {
            onSuccessCallback(data);
          }
        },
        onErrorCallback
      );
    }
    else if (authState.isAuthenticated) {
      checkoutCart(
        pkEcomStore,
        {
          ecomCustomerAddress: cart.shippingAddress,
          shippingOptions,
          installments,
          fkEcomStoreRosterPlayer,
          fundraiserCustomInput
        },
        (data) => {
          webstoreTrackingHelper.initiateCheckout(
            ecomStoreCart,
            data,
            state.ecomStoreTheme.value?.ecomStore
          );
          setCartSecureId(pkEcomStore, data.secureId);

          if (onSuccessCallback) {
            onSuccessCallback(data);
          }
        },
        onErrorCallback
      );
    }
    else {
      checkoutGuestCart(
        pkEcomStore,
        {
          email,
          ecomCustomerAddress: cart.shippingAddress,
          shippingOptions,
          installments,
          ecomStoreCartPackages: cart.ecomStoreCartPackages,
          ecomStoreCartProducts: cart.ecomStoreCartProducts,
          fkEcomStoreRosterPlayer,
          fundraiserCustomInput
        },
        (data) => {
          webstoreTrackingHelper.initiateCheckout(
            ecomStoreCart,
            data,
            state.ecomStoreTheme.value?.ecomStore
          );

          setCartSecureId(pkEcomStore, data.secureId);
          if (onSuccessCallback) {
            onSuccessCallback(data);
          }
        },
        onErrorCallback
      );
    }
  };

  const mockStoreCartCheckout = (
    pkEcomStore,
    { fkEcomStoreRosterPlayer, fundraiserCustomInput },
    onSuccessCallback,
    onErrorCallback
  ) => {
    if (
      ecomStoreCart.checkoutCart.totalInCents !== 0 &&
      ecomStoreCart.checkoutCart.totalInCents / 100 < 5
    ) {
      onErrorCallback('Minimum order amount of $5 required');
    }
    mockEcomStoreCartCheckout(
      pkEcomStore,
      { fkEcomStoreRosterPlayer, fundraiserCustomInput },
      onSuccessCallback,
      onErrorCallback
    );
  };

  const clearCartBySecureId = (pkEcomStore, secureId) => {
    const cart = getGuestCartStorage(pkEcomStore);
    if (cart.secureId === secureId) {
      resetCart(pkEcomStore);
      fetchStoreCart(pkEcomStore);
    }
  };

  const setFundraiser = (
    pkEcomStore,
    { fkEcomStoreRosterPlayer, customInput }
  ) => {
    setCartFundraiser(pkEcomStore, { fkEcomStoreRosterPlayer, customInput });
  };

  return {
    isGuest: authState.tokenAttempted && !authState.isAuthenticated,
    ecomStoreCart,
    isCartFetching,
    shippingAddress,
    shippingCosts,
    isShippingCostsFetching: shippingCostsLoading,
    fetchStoreCart,
    addStoreProducts,
    updateStoreCartProducts,
    removeStoreProducts,
    removeUnavailableStoreCartProducts,
    checkoutStoreCart,
    mockStoreCartCheckout,
    onShowCartProductCustomization,
    getGuestCartStorage,
    clearCartBySecureId,
    resetCart,
    onChangeShippingAddress,
    setFundraiser,
    setCartEmail
  };
};

const getBundleLineItemInfo = (
  ecomStoreCart,
  ecomStoreCartPackage,
  lineItem
) => {
  const cartProducts = ecomStoreCart?.ecomStoreCartProducts ?? [];
  const vendorBundleProducts =
    ecomStoreCartPackage?.ecomStoreProductPackage?.ecomVendorBundle
      ?.ecomVendorBundleProducts ?? [];

  const cartProductMap = new Map(
    cartProducts.map((p) => [p.pkEcomStoreCartProduct, p])
  );
  const vendorBundleProductMap = new Map(
    vendorBundleProducts.map((vbp) => [vbp.fkEcomVendorProduct, vbp])
  );

  const grouped = new Map();
  (lineItem?.bundleItems ?? []).forEach((bi) => {
    const cartProduct = cartProductMap.get(bi.entityId);
    if (!cartProduct) return;

    const latestVariant = ecomStoreCart.latestVersionEcomStoreCartProducts?.find(
      (p) =>
        p.ecomVendorProductVariant.uuid ===
        cartProduct.ecomStoreProductVariant.ecomVendorProductVariant.uuid
    );
    const isUnavailable =
      (!!latestVariant && isCartVariantUnavailable(latestVariant)) ||
      lineItem.isMissingPrintDesigns;

    const requiresProductUpdate = lineItem.isMissingRequiredAddOns;
    const requiresPrintImagesUpdate =
      !ecomStoreCart.printImagesDeferred && lineItem.isMissingPrintImages;

    const vendorProduct =
      cartProduct.ecomStoreProductVariant.ecomStoreProduct.ecomVendorProduct;
    const ecomVendorBundleProduct = vendorBundleProductMap.get(
      vendorProduct.pkEcomVendorProduct
    );

    const bundleItemHasVariantImages =
      vendorProduct?.hasVariantAssignedImages ?? false;

    const bundleItemImage = bundleItemHasVariantImages
      ? bi.variantImage || imageNotAvailablePlaceholder
      : bi.previewImage;

    const quantityLeft =
      cartProduct.ecomStoreProductVariant.ecomVendorProductVariant.quantity;
    const outOfStock = quantityLeft != null && quantityLeft <= 0;
    const tooManyInCart =
      quantityLeft != null && quantityLeft - bi.quantity < 0;

    if (!grouped.has(vendorProduct.pkEcomVendorProduct)) {
      grouped.set(vendorProduct.pkEcomVendorProduct, {
        items: [],
        ecomStoreCartProduct: cartProduct,
        ecomVendorBundleProduct,
        totalQuantity: 0,
        sort: ecomVendorBundleProduct?.sort ?? 0
      });
    }

    const group = grouped.get(vendorProduct.pkEcomVendorProduct);
    group.totalQuantity += bi.quantity;
    group.items.push({
      lineItem: bi,
      cartProduct,
      quantityLeft,
      outOfStock,
      tooManyInCart,
      isUnavailable,
      latestVariant,
      previewImage: bundleItemImage,
      requiresProductUpdate,
      requiresPrintImagesUpdate,
      sortName: bi.addOns[0]?.value ?? '',
      secondSortName: bi.addOns[1]?.value ?? ''
    });
  });

  return [...grouped.values()]
    .map((v) => {
      const itemPerBundleCount = v.items.reduce(
        (r, c) => r + c.lineItem.bundleQuantity,
        0
      );

      return {
        ...v,
        itemPerBundleCount,
        tooManySelected:
          v.ecomVendorBundleProduct &&
          itemPerBundleCount > v.ecomVendorBundleProduct.count,
        notEnoughSelected:
          v.ecomVendorBundleProduct &&
          itemPerBundleCount < v.ecomVendorBundleProduct.count,
        items: v.items.sort(
          (a, b) =>
            a.sortName.localeCompare(b.sortName) ||
            a.secondSortName.localeCompare(b.secondSortName)
        )
      };
    })
    .sort((a, b) => a.sort - b.sort);
};

const useCartLineItemDetails = (ecomStoreCart) => {
  const lineItemInfo = useMemo(
    () =>
      ecomStoreCart?.checkoutCart.lineItems.map((lineItem) => {
        const ecomStoreCartProduct = ecomStoreCart.ecomStoreCartProducts.find(
          (p) => p.pkEcomStoreCartProduct === lineItem.entityId
        );
        const ecomStoreCartPackage = ecomStoreCart.ecomStoreCartPackages.find(
          (p) => p.pkEcomStoreCartPackage === lineItem.entityId
        );
        const latestVersionEcomStoreCartProduct = ecomStoreCart.latestVersionEcomStoreCartProducts?.find(
          (p) =>
            p.ecomVendorProductVariant.uuid ===
            ecomStoreCartProduct?.ecomStoreProductVariant
              .ecomVendorProductVariant.uuid
        );

        const hasVariantAssignedImages =
          ecomStoreCartProduct?.ecomStoreProductVariant.ecomStoreProduct
            .ecomVendorProduct.hasVariantAssignedImages ?? false;
        const previewImage = hasVariantAssignedImages
          ? lineItem.variantImage || imageNotAvailablePlaceholder
          : lineItem.previewImage;

        const packageItemsWithReferences = getBundleLineItemInfo(
          ecomStoreCart,
          ecomStoreCartPackage,
          lineItem
        );

        const quantityLeft = ecomStoreCartProduct
          ? ecomStoreCartProduct.ecomStoreProductVariant
              .ecomVendorProductVariant.quantity
          : null;
        const outOfStock = quantityLeft != null && quantityLeft <= 0;
        const tooManyInCart =
          quantityLeft != null && quantityLeft - lineItem.quantity < 0;

        const hasChanges =
          !!latestVersionEcomStoreCartProduct ||
          (ecomStoreCartPackage &&
            (ecomStoreCartPackage.hasChanges ||
              packageItemsWithReferences.some((pr) =>
                pr.items.some((i) => !!i.latestVariant)
              )));

        const requiresProductUpdate =
          (ecomStoreCartPackage &&
            ecomStoreCartPackage.missingPkEcomVendorProducts.length > 0) ||
          packageItemsWithReferences.some(
            (p) =>
              p.tooManySelected ||
              p.notEnoughSelected ||
              p.items.some(
                (i) =>
                  i.tooManyInCart || i.outOfStock || i.requiresProductUpdate
              )
          ) ||
          lineItem.isMissingRequiredAddOns;

        const isUnavailable =
          lineItem.isMissingPrintDesigns ||
          (ecomStoreCartPackage &&
            (ecomStoreCartPackage.isUnavailable ||
              packageItemsWithReferences.some((pr) =>
                pr.items.some((i) => i.isUnavailable)
              ))) ||
          (latestVersionEcomStoreCartProduct &&
            isCartVariantUnavailable(latestVersionEcomStoreCartProduct));

        const requiresPrintImagesUpdate =
          !ecomStoreCart.printImagesDeferred &&
          (lineItem.isMissingPrintImages ||
            (ecomStoreCartPackage &&
              (ecomStoreCartPackage.isMissingPrintImages ||
                packageItemsWithReferences.some((pr) =>
                  pr.items.some((i) => i.requiresPrintImagesUpdate)
                ))));

        return {
          entityId: lineItem.entityId,
          uuid:
            ecomStoreCartProduct?.ecomStoreProductVariant
              .ecomVendorProductVariant.uuid,
          previewImage,
          hasVariantAssignedImages,
          quantityLeft,
          outOfStock,
          tooManyInCart,
          isUnavailable,
          hasChanges,
          isBundleProduct: lineItem.isBundleProduct,
          requiresProductUpdate,
          requiresPrintImagesUpdate,
          latestVersionEcomStoreCartProduct,
          packageItemsWithReferences,
          ecomStoreCartProduct,
          ecomStoreCartPackage,
          urlHandle: ecomStoreCartProduct
            ? ecomStoreCartProduct.ecomStoreProductVariant.ecomStoreProduct
                .urlHandle
            : ecomStoreCartPackage.urlHandle
        };
      }) || [],
    [ecomStoreCart]
  );

  return { lineItems: lineItemInfo };
};

const useCartProductVariantSelector = (
  ecomStoreProduct,
  { ecomStoreCartPackage, ecomStoreCartProduct, isCartEdit }
) => {
  const [formState, setFormState] = useState({
    initialized: false,
    ecomStoreCartProducts: [],
    adjustments: {},
    optionErrors: [],
    count: 1,
    loading: false
  });
  const {
    ecomStoreCart,
    addStoreProducts,
    updateStoreCartProducts
  } = useCartController();
  const selectOptionInfo = getEcomStoreProductSelectOptions(
    ecomStoreProduct,
    formState.ecomStoreCartProducts,
    ecomStoreCartProduct
  );
  const {
    storeProducts,
    storeProductOptions,
    selectedVariants,
    isBundleProduct
  } = selectOptionInfo;

  useEffect(() => {
    if (ecomStoreProduct) {
      const cartProducts = ecomStoreCartPackage
        ? ecomStoreCart.ecomStoreCartProducts.filter(
            (p) =>
              p.fkEcomStoreCartPackage ===
              ecomStoreCartPackage.pkEcomStoreCartPackage
          )
        : ecomStoreCartProduct
        ? [ecomStoreCartProduct]
        : null;
      const { productStates, adjustments } = cartProducts
        ? // eslint-disable-next-line no-use-before-define
          getInitialPackageProductFormStateData(cartProducts)
        : {};

      setFormState({
        initialized: true,
        ecomStoreCartProducts: productStates
          ? [
              ...productStates,
              ...storeProductOptions
                .filter(
                  (p) =>
                    !productStates.some(
                      (ps) => ps.pkEcomStoreProduct === p.pkEcomStoreProduct
                    )
                )
                .map((p) => {
                  adjustments[p.defaultSelectState.fkEcomVendorProduct] = {
                    change: p.defaultSelectState.count,
                    type: 'new'
                  };

                  return p.defaultSelectState;
                })
            ]
          : storeProductOptions.map((p) => p.defaultSelectState),
        adjustments: adjustments || {},
        optionErrors: [],
        count: ecomStoreCartPackage
          ? ecomStoreCartPackage.count
          : ecomStoreCartProduct?.count || 1,
        loading: false
      });
    }
  }, [ecomStoreProduct, ecomStoreCartProduct, ecomStoreCartPackage]);

  const getInitialPackageProductFormStateData = (cartProducts) => {
    const cartPackage = ecomStoreCartPackage;
    const cartPackageCount = cartPackage?.count || 1;

    const bundleProductsMap = new Map(
      cartPackage?.ecomStoreProductPackage?.ecomVendorBundle?.ecomVendorBundleProducts?.map(
        (bp) => [bp.fkEcomVendorProduct, bp.count]
      ) || []
    );

    const adjustments = {};

    const processedProducts = cartProducts.reduce((acc, p) => {
      const productVariant = p.ecomStoreProductVariant;
      const storeProduct = productVariant.ecomStoreProduct;
      const vendorId = storeProduct.ecomVendorProduct.pkEcomVendorProduct;

      const current = {
        pkEcomStoreCartProduct: p.pkEcomStoreCartProduct,
        pkEcomStoreProduct: storeProduct.pkEcomStoreProduct,
        fkEcomVendorProduct: vendorId,
        count: p.count / cartPackageCount,
        fkEcomStoreCartPackage: cartPackage?.pkEcomStoreCartPackage,
        fkEcomStoreProductPackage:
          cartPackage?.ecomStoreProductPackage?.pkEcomStoreProductPackage,
        selectedOptions: productVariant.ecomVendorProductVariant.ecomVendorProductVariantOptions.map(
          ({ nameSort, name, value }) => ({ nameSort, name, value })
        ),
        ecomStoreCartProductInputAnswers: p.ecomStoreCartProductInputAnswers.map(
          (a) => ({
            ...a,
            isAutopopulated: ecomStoreProduct.ecomStoreProductInputAnswers.some(
              (is) =>
                is.fkRegFormControlType === a.fkRegFormControlType &&
                (is.fkEcomVendorProductInput === a.fkEcomVendorProductInput ||
                  is.label.toLowerCase() === a.label.toLowerCase())
            )
          })
        ),
        ecomOrderProductPrintImages: p.ecomOrderProductPrintImages.map((i) => ({
          ...i
        }))
      };

      const bundleMaxCount = bundleProductsMap.get(vendorId);

      if (!acc.has(vendorId)) {
        acc.set(vendorId, {
          totalCount: 0,
          originalTotal: 0,
          states: []
        });
      }

      const group = acc.get(vendorId);

      group.originalTotal += current.count;

      if (bundleMaxCount !== undefined) {
        const remainingCapacity = bundleMaxCount - group.totalCount;
        const allowedCount = Math.min(current.count, remainingCapacity);

        if (allowedCount > 0) {
          group.states.push({ ...current, count: allowedCount });
          group.totalCount += allowedCount;
        }
      }
      else {
        group.states.push(current);
        group.totalCount += current.count;
      }

      return acc;
    }, new Map());

    // Post-process to fill missing bundle quantities
    for (const [vendorId, group] of processedProducts.entries()) {
      const bundleMaxCount = bundleProductsMap.get(vendorId);

      if (bundleMaxCount !== undefined) {
        if (group.totalCount < bundleMaxCount && group.states.length > 0) {
          const remaining = bundleMaxCount - group.totalCount;

          group.states[0].count += remaining;
          group.totalCount += remaining;
        }

        const delta = group.totalCount - group.originalTotal;

        if (delta !== 0) {
          adjustments[vendorId] = {
            change: delta,
            type: delta > 0 ? 'added' : 'removed'
          };
        }
      }
    }

    const productStates = [...processedProducts.values()].flatMap(
      (p) => p.states
    );

    return {
      productStates,
      adjustments
    };
  };

  const getRequestData = () =>
    isBundleProduct
      ? {
          includeShipping: isCartEdit,
          ecomStoreCartPackages: [
            {
              pkEcomStoreCartPackage:
                ecomStoreCartPackage?.pkEcomStoreCartPackage,
              fkEcomStoreProductPackage:
                ecomStoreProduct.ecomStoreProductPackage
                  .pkEcomStoreProductPackage,
              count: formState.count,
              ecomStoreCartProducts: formState.ecomStoreCartProducts
                .reduce((r, c) => {
                  const existing = r.find(
                    (f) =>
                      f.pkEcomStoreProduct === c.pkEcomStoreProduct &&
                      f.selectedOptions.every((op) =>
                        c.selectedOptions.find(
                          (sop) =>
                            sop.name === op.name && sop.value === op.value
                        )
                      ) &&
                      matchInputAnswers(
                        f.ecomStoreCartProductInputAnswers,
                        c.ecomStoreCartProductInputAnswers
                      ) &&
                      matchDesignAnswers(
                        f.ecomOrderProductPrintImages,
                        c.ecomOrderProductPrintImages
                      )
                  );
                  if (existing) {
                    existing.count += c.count;
                  }
                  else {
                    const existingCartProduct = ecomStoreCartPackage
                      ? ecomStoreCart.ecomStoreCartProducts.find(
                          (p) =>
                            p.fkEcomStoreCartPackage ===
                              ecomStoreCartPackage.pkEcomStoreCartPackage &&
                            p.ecomStoreProductVariant.ecomStoreProduct
                              .pkEcomStoreProduct === c.pkEcomStoreProduct &&
                            p.ecomStoreProductVariant.ecomVendorProductVariant.ecomVendorProductVariantOptions.every(
                              (op) =>
                                c.selectedOptions.find(
                                  (sop) =>
                                    sop.name === op.name &&
                                    sop.value === op.value
                                )
                            ) &&
                            matchInputAnswers(
                              p.ecomStoreCartProductInputAnswers,
                              c.ecomStoreCartProductInputAnswers
                            ) &&
                            matchDesignAnswers(
                              p.ecomOrderProductPrintImages,
                              c.ecomOrderProductPrintImages
                            ) &&
                            !r.find(
                              (rf) =>
                                rf.fkEcomStoreCartProduct ===
                                p.pkEcomStoreCartProduct
                            )
                        )
                      : null;

                    r.push({
                      ...c,
                      pkEcomStoreCartProduct:
                        existingCartProduct?.pkEcomStoreCartProduct || null
                    });
                  }
                  return r;
                }, [])
                .map((p) => {
                  const {
                    product,
                    selectedVariant
                  } = getSelectedEcomStoreProductVariant(p, storeProducts);
                  return {
                    pkEcomStoreCartProduct: p.pkEcomStoreCartProduct,
                    uuid: selectedVariant?.ecomVendorProductVariant.uuid,
                    fkEcomStoreProductPackage:
                      ecomStoreProduct.ecomStoreProductPackage
                        ?.pkEcomStoreProductPackage,
                    count: p.count,
                    ecomStoreCartProductInputAnswers:
                      p.ecomStoreCartProductInputAnswers,
                    ecomOrderProductPrintImages: p.ecomOrderProductPrintImages,
                    ecomStoreProduct: product
                  };
                })
                .filter((p) => !!p.uuid)
            }
          ]
        }
      : {
          includeShipping: isCartEdit,
          ecomStoreCartProducts: [
            {
              pkEcomStoreCartProduct:
                ecomStoreCartProduct?.pkEcomStoreCartProduct,
              uuid: selectedVariants[0]?.ecomVendorProductVariant.uuid,
              count: formState.count,
              ecomStoreCartProductInputAnswers:
                formState.ecomStoreCartProducts[0]
                  .ecomStoreCartProductInputAnswers,
              ecomOrderProductPrintImages:
                formState.ecomStoreCartProducts[0].ecomOrderProductPrintImages,

              ecomStoreProduct
            }
          ]
        };

  const onAddProductToCart = (onAddOrUpdateCallback = null) => {
    const requestData = getRequestData();

    setFormState({ ...formState, optionErrors: [], loading: true });
    addStoreProducts(
      ecomStoreProduct.fkEcomStore,
      requestData,
      (d) => {
        setFormState({
          ...formState,
          optionErrors: [],
          loading: false
        });

        if (onAddOrUpdateCallback) {
          onAddOrUpdateCallback(d);
        }
      },
      (error, type) => {
        if (type === 'input') {
          setFormState({
            ...formState,
            optionErrors: [error],
            loading: false
          });
        }
        else {
          triggerNotification(error);
          setFormState({
            ...formState,
            optionErrors: [],
            loading: false
          });
        }
      }
    );
  };

  const onUpdateCartProduct = (onAddOrUpdateCallback) => {
    const requestData = getRequestData();

    setFormState({ ...formState, optionErrors: [], loading: true });
    updateStoreCartProducts(
      ecomStoreProduct.fkEcomStore,
      requestData,
      (d) => {
        setFormState({
          ...formState,
          optionErrors: [],
          loading: false
        });

        if (onAddOrUpdateCallback) {
          onAddOrUpdateCallback(d);
        }
      },
      (error, type) => {
        if (type === 'input') {
          setFormState({
            ...formState,
            optionErrors: [error],
            loading: false
          });
        }
        else {
          triggerNotification(error);
          setFormState({
            ...formState,
            optionErrors: [],
            loading: false
          });
        }
      }
    );
  };

  const getTransformedDesignAnswersToNewDesign = (
    pkEcomStoreProduct,
    updatedEcomStoreCartProductsState,
    currentProductState
  ) => {
    const designStoreProduct = ecomStoreProduct.ecomStoreProductPackage
      ? ecomStoreProduct.ecomStoreProductPackage.ecomStoreProducts.find(
          (bp) => bp.pkEcomStoreProduct === pkEcomStoreProduct
        )
      : ecomStoreProduct;

    if (!designStoreProduct) {
      return currentProductState.ecomOrderProductPrintImages;
    }

    const nextSelectedVariantOptions = getEcomStoreProductSelectOptions(
      designStoreProduct,
      updatedEcomStoreCartProductsState,
      ecomStoreCartProduct
    );
    const nextVariant = nextSelectedVariantOptions.selectedVariants.find(
      (v) => v.fkEcomStoreProduct === pkEcomStoreProduct
    );
    if (nextVariant) {
      const {
        selectedDesigns: nextSelectedDesigns
      } = getSelectedEcomStoreProductDesigns(
        designStoreProduct,
        nextVariant.ecomVendorProductVariant.pkEcomVendorProductVariant
      );

      const allDesignInputs = designStoreProduct.ecomVendorProduct.ecomVendorProductPrintDesigns.flatMap(
        (d) => d.ecomVendorProductPrintDesignItems
      );

      return currentProductState.ecomOrderProductPrintImages.map((i) => ({
        ...i,
        ecomOrderProductPrintImageAnswers: i.ecomOrderProductPrintImageAnswers.map(
          (a) => {
            const designInput = allDesignInputs.find(
              (di) =>
                di.pkEcomVendorProductPrintDesignItem ===
                a.fkEcomVendorProductPrintDesignItem
            );

            if (!designInput) return a;

            const currentDesignLabel =
              designInput.itemProperties
                .find((ip) => ip.name === 'inputLabel')
                ?.value.trim()
                .toLowerCase() || '';

            const matchingSelectedDesign = nextSelectedDesigns.find(
              (d) => d.fkEcomImagePosition === i.fkEcomImagePosition
            );
            if (!matchingSelectedDesign) return a;

            const matchingDesignInput = matchingSelectedDesign.ecomVendorProductPrintDesignItems.find(
              (f) =>
                f.itemProperties.some(
                  (ip) =>
                    ip.name === 'inputLabel' &&
                    ip.value.trim().toLowerCase() === currentDesignLabel
                )
            );

            return {
              ...a,
              src: '',
              fkEcomVendorProductPrintDesignItem:
                matchingDesignInput?.pkEcomVendorProductPrintDesignItem ||
                a.fkEcomVendorProductPrintDesignItem
            };
          }
        )
      }));
    }

    return currentProductState.ecomOrderProductPrintImages;
  };

  const onVariantChange = (pkEcomStoreProduct, options) => {
    let updatedEcomStoreCartProductsState = formState.ecomStoreCartProducts.map(
      (p) =>
        p.pkEcomStoreProduct === pkEcomStoreProduct
          ? {
              ...p,
              selectedOptions: options
            }
          : p
    );
    const currentProductState = updatedEcomStoreCartProductsState.find(
      (p) => p.pkEcomStoreProduct === pkEcomStoreProduct
    );

    if (
      currentProductState &&
      currentProductState.ecomOrderProductPrintImages.length > 0
    ) {
      const updatedPrintImages = getTransformedDesignAnswersToNewDesign(
        pkEcomStoreProduct,
        updatedEcomStoreCartProductsState,
        currentProductState
      );

      updatedEcomStoreCartProductsState = updatedEcomStoreCartProductsState.map(
        (p) =>
          p.pkEcomStoreProduct === pkEcomStoreProduct
            ? {
                ...p,
                ecomOrderProductPrintImages: updatedPrintImages
              }
            : p
      );
    }

    setFormState({
      ...formState,
      ecomStoreCartProducts: updatedEcomStoreCartProductsState,
      optionErrors: []
    });
  };

  const onInputAnswerChange = (pkEcomStoreProduct, answers) =>
    setFormState({
      ...formState,
      ecomStoreCartProducts: formState.ecomStoreCartProducts.map((p) =>
        p.pkEcomStoreProduct === pkEcomStoreProduct
          ? {
              ...p,
              ecomStoreCartProductInputAnswers: answers
            }
          : p
      ),
      optionErrors: []
    });

  const onDesignAnswerChange = (pkEcomStoreProduct, printImages) => {
    setFormState({
      ...formState,
      ecomStoreCartProducts: formState.ecomStoreCartProducts.map((p) =>
        p.pkEcomStoreProduct === pkEcomStoreProduct
          ? {
              ...p,
              ecomOrderProductPrintImages: printImages
            }
          : p
      ),
      optionErrors: []
    });
  };

  const onBundleProductVariantSelect = (pkEcomStoreProduct, optionStates) => {
    setFormState({
      ...formState,
      ecomStoreCartProducts: [
        ...formState.ecomStoreCartProducts.filter(
          (f) => f.pkEcomStoreProduct !== pkEcomStoreProduct
        ),
        ...optionStates
      ],
      optionErrors: []
    });
  };

  const onQuantityChange = (count) => {
    setFormState({
      ...formState,
      optionErrors: [],
      count
    });
  };

  return {
    formState,
    selectOptionInfo,
    onAddProductToCart,
    onUpdateCartProduct,
    onVariantChange,
    onInputAnswerChange,
    onDesignAnswerChange,
    onBundleProductVariantSelect,
    onQuantityChange
  };
};

export {
  useCartController,
  useCartLineItemDetails,
  useCartProductVariantSelector
};
